import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { useMutation } from '@globalconnect/query';
import { untypedIsEnabled } from '@ip-only/client-features';
import { ObjectWithProps } from '@ip-only/front-end-library';
import { combineLatest, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { DetailedErrorFeedbackAlertService } from '../../../shared/feedback-alert/service/detailed-error-feedback-alert.service';
import { FeedbackAlertService } from '../../../shared/feedback-alert/service/feedback-alert.service';
import { CompleteReportConfirmationState } from '../../../shared/installation-report-content-v2/complete-report-confirmation-modal/complete-report-confirmation-modal.component';
import {
  CombinedDataV2,
  InstallationReportCompleteStatus,
  InstallationReportOrderDetail,
  InstallationReportTabNames,
} from '../../../shared/installation-report-content-v2/installation-report-content-entities';
import { InstallationReportReadOnlyService } from '../../../shared/installation-report-content-v2/service/installation-report-read-only.service';
import { UpdateReportService } from '../../../shared/installation-report-content-v2/service/update-report.service';
import {
  onErrorCompleteInstallationReport,
  onErrorUpdateInstallationReport,
  onSuccessUpdateInstallationReport,
} from '../../../shared/installation-report-content-v2/utils/handle-gcquery-request';
import { updateInstallationReportRequestBody } from '../../../shared/installation-report-content-v2/utils/handle-request-objects';
import {
  getCompleteReportPrimaryButtonLabelKey,
  getCompleteReportSecondaryButtonTooltipKey,
  getInstallationReportCompleteButtonState,
  InstallationReportCompleteButtonState,
  isCompleteReportPrimaryButtonEnabled,
} from '../../../shared/installation-report-content-v2/utils/is-installation-report-fully-complete';
import {
  IR_INSTALLATION_REPORT_CLOSED,
  IR_INSTALLATION_REPORT_COMPLETED,
  IR_INSTALLATION_REPORT_SAVE_ATTEMPT,
  IR_INSTALLATION_REPORT_UPDATED,
} from '../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../shared/posthug-analytics/analytics.service';
import { isTopLevelDomainFI } from '../../../shared/util/deploy-helper';
import { CONSTRUCTION_RESPONSIBLE } from '../../../user-roles-def/user-roles';
import { UserService } from '../../../user/user.service';
import { InstallationReportingV2Service } from '../../service/sdu/installation-reporting-v2.service';

@Component({
  selector: 'app-installation-report-modal-v2',
  templateUrl: './installation-report-modal-v2.component.html',
  styleUrls: ['./installation-report-modal-v2.component.scss'],
  standalone: false,
})
export class InstallationReportModalV2Component implements OnInit, OnDestroy {
  @Input() ifs: string;
  @Input() deliveryOrderId: string | null | undefined;

  deliveryItem$: Observable<InstallationReportOrderDetail>;
  combinedData$: Observable<CombinedDataV2>;
  completeReportButtonState$: Observable<InstallationReportCompleteButtonState | null>;

  form: UntypedFormGroup = this.formBuilder.group({});
  installationReportTabNames: InstallationReportTabNames;

  arcEnabled = untypedIsEnabled('arc');
  isDeliveryPortalFi = isTopLevelDomainFI();

  readonly isCompleteReportPrimaryButtonEnabled =
    isCompleteReportPrimaryButtonEnabled;

  confirmationState: 'closed' | CompleteReportConfirmationState = 'closed';

  private completeMutationIsUnlock = false;

  installationReportUpdateMutation = useMutation(
    this.installationReportV2Service.updateInstallationReportV2$,
    {
      onSuccess: () => {
        if (this.deliveryOrderId) {
          onSuccessUpdateInstallationReport(
            this.ifs,
            this.deliveryOrderId,
            this.feedbackAlertService,
          );
        }
        this.installationReportV2Service.retriggerGetAll$.next(null);
        this.saveMacAddress();
        this.updateReportService.setReportSaved();
        this.analyticsService.logEvent(IR_INSTALLATION_REPORT_UPDATED);
      },
      onError: () => onErrorUpdateInstallationReport(this.feedbackAlertService),
    },
  );

  installationReportCompleteMutation = useMutation(
    this.installationReportV2Service.completeInstallationReportV2$,
    {
      onSuccess: () => {
        if (this.deliveryOrderId) {
          onSuccessUpdateInstallationReport(
            this.ifs,
            this.deliveryOrderId,
            this.feedbackAlertService,
          );
        }
        this.installationReportV2Service.retriggerGetAll$.next(null);
        this.analyticsService.logEvent(IR_INSTALLATION_REPORT_COMPLETED);
        this.confirmationState = this.completeMutationIsUnlock
          ? 'closed'
          : 'completed';
      },
      onError: (error) => {
        onErrorCompleteInstallationReport(
          error,
          this.detailedErrorFeedbackAlertService,
          this.feedbackAlertService,
        );
        this.confirmationState = this.completeMutationIsUnlock
          ? 'closed'
          : 'accept';
      },
    },
  );

  private cancelSubs$ = new Subject<void>();

  constructor(
    private readonly installationReportV2Service: InstallationReportingV2Service,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly feedbackAlertService: FeedbackAlertService,
    private readonly detailedErrorFeedbackAlertService: DetailedErrorFeedbackAlertService,
    private readonly analyticsService: AnalyticsService,
    private readonly updateReportService: UpdateReportService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly readOnlyService: InstallationReportReadOnlyService,
  ) {}

  ngOnInit(): void {
    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('no-scroll');
    if (this.deliveryOrderId) {
      this.installationReportV2Service.setActiveReportContext(
        this.ifs,
        this.deliveryOrderId,
      );
    }
    this.getData();
  }

  ngOnDestroy(): void {
    this.installationReportV2Service.clearActiveReportContext();
    this.cancelSubs$.next();
    this.cancelSubs$.complete();
    document.body.classList.remove('no-scroll');
    document.documentElement.classList.remove('no-scroll');
  }

  getData() {
    if (this.deliveryOrderId) {
      this.combinedData$ = combineLatest([
        this.installationReportV2Service.getInstallationReportByIfsAndOpportunityDeliveryOrderIdV2(
          this.ifs,
          this.deliveryOrderId,
        ),
        this.installationReportV2Service.getDeliveryItem$(
          this.ifs,
          this.deliveryOrderId,
        ),
      ]).pipe(
        takeUntil(this.cancelSubs$),
        map(([installationReport, deliveryItem]) => ({
          installationReport,
          deliveryItem,
        })),
      );

      this.completeReportButtonState$ = this.combinedData$.pipe(
        takeUntil(this.cancelSubs$),
        map(({ installationReport }) =>
          getInstallationReportCompleteButtonState(
            this.hasPermittedRoles(),
            installationReport.data,
          ),
        ),
      );
    }
  }

  onProceed() {
    this.readOnlyService.whenEditable(() => {
      if (!this.form.valid || !this.deliveryOrderId) {
        this.feedbackAlertService.showAlert(
          'critical',
          'installationReportV2.modal.onProceedError',
        );
        return;
      }
      this.installationReportUpdateMutation.mutate(
        updateInstallationReportRequestBody(this.form, this.deliveryOrderId),
        this.form.get('id')?.value,
      );
    });
    this.analyticsService.logEvent(IR_INSTALLATION_REPORT_SAVE_ATTEMPT);
  }

  getCompleteButtonLabelKeyForState(
    state: InstallationReportCompleteButtonState | null | undefined,
  ): string | null {
    return getCompleteReportPrimaryButtonLabelKey(
      this.isDeliveryPortalFi,
      this.arcEnabled,
      state ?? null,
    );
  }

  getCompleteButtonTooltipKeyForState(
    state: InstallationReportCompleteButtonState | null | undefined,
  ): string | null {
    return getCompleteReportSecondaryButtonTooltipKey(
      this.isDeliveryPortalFi,
      this.arcEnabled,
      state ?? null,
    );
  }

  isCombinedDataLoading(combinedData: CombinedDataV2): boolean {
    return (
      combinedData.installationReport.isLoading ||
      combinedData.deliveryItem.isLoading
    );
  }

  onCompleteReportButtonClick(
    state: InstallationReportCompleteButtonState | null | undefined,
  ) {
    if (state === 'completeEnabled') {
      this.confirmationState = 'accept';
      return;
    }
    this.performCompleteOrUnlock();
  }

  onConfirmComplete() {
    this.performCompleteOrUnlock();
  }

  onDismissConfirmation() {
    this.confirmationState = 'closed';
  }

  performCompleteOrUnlock() {
    if (!this.deliveryOrderId) {
      this.feedbackAlertService.showAlert(
        'critical',
        'installationReportV2.modal.onProceedError',
      );
      this.analyticsService.logEvent(IR_INSTALLATION_REPORT_COMPLETED);
      return;
    }

    this.combinedData$
      .pipe(
        take(1),
        takeUntil(this.cancelSubs$),
        map(({ installationReport }) => ({
          status: installationReport.data?.isCompleted
            ? ({ pathValue: 'unlock' } as InstallationReportCompleteStatus)
            : ({ pathValue: 'complete' } as InstallationReportCompleteStatus),
          isUnlock: !!installationReport.data?.isCompleted,
          reportId:
            this.form.get('id')?.value ?? installationReport.data?.id ?? null,
        })),
      )
      .subscribe(({ status, isUnlock, reportId }) => {
        if (!reportId) {
          this.feedbackAlertService.showAlert(
            'critical',
            'installationReportV2.modal.onProceedError',
          );
          return;
        }
        // Disabled forms are not "valid" in Angular; unlock does not submit form data.
        if (!isUnlock && !this.form.valid) {
          this.feedbackAlertService.showAlert(
            'critical',
            'installationReportV2.modal.onProceedError',
          );
          return;
        }
        this.completeMutationIsUnlock = isUnlock;
        this.installationReportCompleteMutation.mutate(status, reportId);
      });
    this.analyticsService.logEvent(IR_INSTALLATION_REPORT_COMPLETED);
  }

  saveMacAddress() {
    if (
      ((this.form.value as ObjectWithProps).PRE_INSTALLATION as ObjectWithProps)
        ?.macAddress
    ) {
      this.updateReportService.setSavedMacAddress(
        (
          (this.form.value as ObjectWithProps)
            .PRE_INSTALLATION as ObjectWithProps
        )?.macAddress,
      );
    }
  }

  hasPermittedRoles() {
    return this.userService.checkAccess([CONSTRUCTION_RESPONSIBLE]);
  }

  closeModal() {
    this.analyticsService.logEvent(IR_INSTALLATION_REPORT_CLOSED);
    this.router.navigateByUrl(`/installation-reporting/${this.ifs}`);
  }
}
